import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'
import { Icon, type IconName } from './Icon'

export interface HuntStatsBarProps {
  pendingInvitations: number
  upcomingHunts: number
  endedHunts: number
  /** Count of downed game (animals). Surfaced as the "Downed game" tile. */
  harvestToDate: number
  /** While true, the Downed game tile masks its value so a real 0 isn't confused with loading. */
  harvestLoading?: boolean
  // Each tile counts a list the hunter can open. A tile without its handler
  // stays a plain, non-interactive card rather than a button that does nothing.
  // Downed game has no handler at all: it counts animals, not hunts, so there
  // is no list to send anyone to.
  onViewInvitations?: () => void
  onViewUpcoming?: () => void
  onViewEnded?: () => void
}

export function HuntStatsBar({ pendingInvitations, upcomingHunts, endedHunts, harvestToDate, harvestLoading, onViewInvitations, onViewUpcoming, onViewEnded }: HuntStatsBarProps) {
  const { t } = useTranslation('hunts')
  const hasInvites = pendingInvitations > 0
  return (
    <div
      className="wh-grid-stats"
      style={{
        marginBottom: 20,
        gridTemplateColumns: hasInvites ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)',
      }}
    >
      {hasInvites && (
        <StatTile
          icon="bell"
          label={t('stats.pendingInvitations')}
          value={pendingInvitations}
          tone="#ff681f"
          suffix={t('stats.toReply')}
          onClick={onViewInvitations}
        />
      )}
      <StatTile icon="hunts" label={t('stats.upcomingHunts')} value={upcomingHunts} tone="#20502b" onClick={onViewUpcoming} />
      <StatTile icon="clock" label={t('stats.endedHunts')} value={endedHunts} tone="#747a76" onClick={onViewEnded} />
      <StatTile
        icon="target"
        label={t('stats.downedGame')}
        value={harvestToDate}
        tone="#ff681f"
        suffix={t('stats.animals')}
        loading={harvestLoading}
        valueTestId="downed-game-value"
      />
    </div>
  )
}

interface StatTileProps {
  icon: IconName
  label: string
  value: number
  tone: string
  suffix?: string
  loading?: boolean
  valueTestId?: string
  onClick?: () => void
}

function StatTile({ icon, label, value, tone, suffix, loading, valueTestId, onClick }: StatTileProps) {
  const interactive = typeof onClick === 'function'
  const Tag = interactive ? 'button' : 'div'
  return (
    <Tag
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      aria-label={interactive ? label : undefined}
      className="wh-card"
      style={{
        padding: 18,
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        background: 'inherit',
        border: 'inherit',
        textAlign: 'left',
        cursor: interactive ? 'pointer' : undefined,
        font: 'inherit',
        color: 'inherit',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: tone + '1a',
          color: tone,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={icon} size={22} />
      </div>
      <div>
        <div style={{ fontSize: 11, color: COLORS.outline, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>{label}</div>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 28, fontWeight: 500, lineHeight: 1.1 }}>
          {loading ? (
            <span data-testid={valueTestId} style={{ color: COLORS.outline }}>—</span>
          ) : (
            <>
              <span data-testid={valueTestId}>{value}</span>
              {suffix && <span style={{ fontSize: 13, fontWeight: 500, color: COLORS.outline, marginLeft: 6 }}>{suffix}</span>}
            </>
          )}
        </div>
      </div>
    </Tag>
  )
}
